import { useState } from 'react';
import { ArrowUpRight, Github, Linkedin, Mail } from 'lucide-react';
import type { PortfolioData } from '../../firebase/types/portfolio.types';
import { opensInNewTab, workSectionHref } from './model';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('') || '•';
}

function heroLead(data: PortfolioData): string | undefined {
  const positioning = data.positioningLine?.trim();
  if (positioning) return positioning;
  const summary = data.professionalSummary?.trim();
  if (!summary) return undefined;
  const line = summary.split('\n').map((item) => item.trim()).find(Boolean);
  if (!line) return undefined;
  return line.length > 180 ? `${line.slice(0, 177).trimEnd()}…` : line;
}

function Portrait({ name, src }: { name: string; src: string }) {
  const [failed, setFailed] = useState(false);

  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-3 z-10 h-6 w-6 border-l border-t border-signal" aria-hidden="true" />
      <span className="pointer-events-none absolute bottom-3 right-3 z-10 h-6 w-6 border-b border-r border-signal/70" aria-hidden="true" />
      <div className="overflow-hidden rounded-3xl border border-line bg-elevated shadow-[var(--shadow)]">
        {failed ? (
          <div className="flex aspect-[4/5] items-center justify-center text-5xl font-semibold text-faint">
            {initials(name)}
          </div>
        ) : (
          <img
            src={src}
            alt={name}
            className="aspect-[4/5] w-full object-cover"
            onError={() => setFailed(true)}
          />
        )}
      </div>
    </div>
  );
}

export default function Hero({ data }: { data: PortfolioData }) {
  const lead = heroLead(data);
  const ctaLink = data.heroCtaLink?.trim();
  const ctaLabel = data.heroCtaText?.trim() || (ctaLink ? 'Book a call' : '');
  const workHref = workSectionHref(data);
  const stats = [
    data.yearsOfExperience?.trim() ? { label: 'Experience', value: data.yearsOfExperience.trim() } : null,
    data.projectsCompletedCount?.trim() ? { label: 'Projects shipped', value: data.projectsCompletedCount.trim() } : null,
    data.clientsServedCount?.trim() ? { label: 'Clients', value: data.clientsServedCount.trim() } : null,
  ].filter((stat): stat is { label: string; value: string } => !!stat);
  const showPortrait = Boolean(data.avatarUrl?.trim());

  return (
    <section className="pb-6 pt-[calc(5.25rem+env(safe-area-inset-top))] md:pb-8">
      <div className="shell">
        <div className={`stage grid items-center gap-6 ${showPortrait ? 'lg:grid-cols-12 lg:gap-8' : ''}`}>
          <div className={showPortrait ? 'lg:col-span-7' : ''}>
            {data.title ? (
              <p className="mb-3 flex items-center gap-3 text-sm text-muted md:text-base">
                <span className="h-px w-8 bg-signal" aria-hidden="true" />
                {data.title}
              </p>
            ) : null}
            <h1 className="hero-name max-w-5xl break-words text-display-hero font-semibold text-balance">{data.fullName}</h1>
            {lead ? <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted md:text-lg">{lead}</p> : null}
            {stats.length ? (
              <dl className="mt-4 flex flex-wrap gap-2">
                {stats.map((stat) => (
                  <div key={stat.label} className="min-w-[7.5rem] rounded-xl border border-line bg-canvas/50 px-3 py-2">
                    <dt className="text-xs text-faint">{stat.label}</dt>
                    <dd className="mt-1 text-lg font-semibold tracking-tight">{stat.value}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {ctaLink && ctaLabel ? (
                <a
                  href={ctaLink}
                  className="btn btn-primary"
                  {...(opensInNewTab(ctaLink) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {ctaLabel}
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
              ) : null}
              {ctaLink && workHref ? (
                <a href={workHref} className="btn btn-secondary">
                  View My Work
                </a>
              ) : null}
              {data.resumePdfUrl ? (
                <a href={data.resumePdfUrl} className="btn btn-secondary" target="_blank" rel="noopener noreferrer">
                  Resume
                </a>
              ) : null}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {data.email ? (
                <a href={`mailto:${data.email}`} className="icon-btn" aria-label={`Email ${data.fullName}`}>
                  <Mail className="h-4 w-4" strokeWidth={1.75} />
                </a>
              ) : null}
              {data.github ? (
                <a href={data.github} className="icon-btn" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                  <Github className="h-4 w-4" strokeWidth={1.75} />
                </a>
              ) : null}
              {data.linkedin ? (
                <a href={data.linkedin} className="icon-btn" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <Linkedin className="h-4 w-4" strokeWidth={1.75} />
                </a>
              ) : null}
            </div>
          </div>
          {showPortrait ? (
            <div className="lg:col-span-5">
              <Portrait name={data.fullName} src={data.avatarUrl!.trim()} />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
